import React, { useEffect, useRef, useState } from 'react';
import Icon from '@/components/ui/Icon';
import useSpeechstream from '@/components/shared/TextToSpeechButton/useSpeechStream';
import LanguageSelectMenu from './LanguageSelectMenu';
import ButtonWithAdjacentDropdown from '../Button/ButtonWithAdjacentDropdown';

interface TranslateButtonProps {
  locale: Locale;
  userLanguage: string;
}

const TranslateButton = ({ locale, userLanguage }: TranslateButtonProps) => {
  const {
    loaded,
    setTranslationDestination,
    setTranslationSource,
    translateSelection,
  } = useSpeechstream({ idToRead: '', locale });
  if (!loaded) return null;

  const savedSelectionRef = useRef(null);
  const [selectedLanguage, setSelectedLanguage] = useState(userLanguage);
  const [selectedText, setSelectedText] = useState('');

  const isEnabled = selectedText?.length > 0;

  const onSelectionChanged = () => {
    const selection = window.getSelection();

    setSelectedText(selection?.toString() ?? '');
  };

  useEffect(() => {
    document.addEventListener('selectionchange', onSelectionChanged);
    return () => {
      document.removeEventListener('selectionchange', onSelectionChanged);
    };
  }, []);

  const tooltipLabel = () => {
    if (selectedText.length > 0) {
      if (selectedLanguage) {
        return `Translate selected text into ${selectedLanguage}.`;
      }
      return 'Choose a language to translate the selected text.';
    }

    return 'Select text to translate into another language';
  };

  const restoreSelection = () => {
    const selection = window.getSelection();

    if (savedSelectionRef?.current?.toString()?.length === 0) {
      return;
    }

    selection.removeAllRanges();
    selection.addRange(savedSelectionRef.current);
  };

  const saveSelection = () => {
    const selection = window.getSelection();
    if (selection && selection.rangeCount > 0) {
      savedSelectionRef.current = selection.getRangeAt(0).cloneRange();
    } else {
      savedSelectionRef.current = null;
    }
  };

  const translateSelectedText = language => (
    setTranslationSource(locale === 'en' ? 'English' : 'Spanish')?.then(() => {
      setTranslationDestination(language).then((res) => {
        setSelectedLanguage(res.englishDescription);
        restoreSelection();
        translateSelection();
      });
    })
  );

  const handleButtonClick = async () => {
    saveSelection();

    if (!isEnabled) return;

    await translateSelectedText(selectedLanguage);
  };

  return (
    <div className="tw-flex">
      <ButtonWithAdjacentDropdown
        ariaLabel={tooltipLabel()}
        disabled={!isEnabled}
        onAdjacentButtonClick={saveSelection}
        onButtonClick={handleButtonClick}
      >
        <ButtonWithAdjacentDropdown.ButtonContent>
          <Icon fill={isEnabled ? '#522e91' : '#ccc'} icon="Translate" />
        </ButtonWithAdjacentDropdown.ButtonContent>
        <ButtonWithAdjacentDropdown.DropdownContent
          className="tw-w-auto"
        >
          <ButtonWithAdjacentDropdown.Close asChild>
            <LanguageSelectMenu
              locale={locale}
              restoreSelection={restoreSelection}
              selectedLanguage={selectedLanguage}
              setLanguage={language => translateSelectedText(language)}
            />
          </ButtonWithAdjacentDropdown.Close>
        </ButtonWithAdjacentDropdown.DropdownContent>
      </ButtonWithAdjacentDropdown>
    </div>
  );
};

export default TranslateButton;
